import React, { useMemo, useCallback, useState, useEffect } from "react";
import useTripStore from "@/stores/tripBuilder/useTrip";
import { Tooltip } from "@heroui/tooltip";
import { TripUser } from "@/types/trip";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { cn } from "@/lib/utils";
import { Trash, Users, UserPlus2 } from "lucide-react";
import { AvatarImage } from "@/components/ui/DefaultAvatar";
import useBudgetStore from "@/stores/tripBuilder/useBudgetStore";

interface FlightInformationProps {
  users?: TripUser[];
  locationId: string;
}

const TripParticipants: React.FC<FlightInformationProps> = ({
  users = [],
  locationId,
}) => {
  const updateLocation = useTripStore((state) => state.updateLocation);
  const tripData = useTripStore((state) => state.tripData);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [locationParticipants, setlocationParticipants] = useState<TripUser[]>(
    []
  );
  const { calculateBudget } = useBudgetStore();

  // Get location-specific participants
  useEffect(() => {
    if (!tripData || !users) return;
    const locationParticipantIds =
      tripData.locations.find((location) => location.id === locationId)
        ?.participants || [];
    setlocationParticipants(
      users.filter((user) =>
        locationParticipantIds.some(
          (participant) => participant.id === user.user_id
        )
      )
    );
  }, [tripData, users, locationId]);

  // Get available users (users not already participating in this location)
  const availableUsers = useMemo(() => {
    if (!tripData || !users) return [];
    const locationParticipantIds =
      tripData.locations.find((location) => location.id === locationId)
        ?.participants || [];
    return users.filter(
      (user) =>
        !locationParticipantIds.some(
          (participant) => participant.id === user.user_id
        )
    );
  }, [tripData, users, locationId]);

  const handleAddParticipant = useCallback(
    (user: TripUser, e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();

      if (!tripData) return;

      const currentParticipants =
        tripData?.locations.find((location) => location.id === locationId)
          ?.participants || [];
      if (
        !currentParticipants.some(
          (participant) => participant.id === user.user_id
        )
      ) {
        updateLocation(locationId, {
          participants: [
            ...currentParticipants,
            {
              id: user.user_id,
              name: `${user.first_name} ${user.last_name}`,
              profile_image: user.profile_image,
            },
          ],
        });

        // Update local state
        setlocationParticipants([...locationParticipants, user]);
        // Recalculate budget
        calculateBudget(locationId);
      }

      setIsDropdownOpen(false);
    },
    [
      tripData,
      locationId,
      updateLocation,
      locationParticipants,
      calculateBudget,
    ]
  );

  const handleRemoveParticipant = useCallback(
    (user: TripUser, e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();

      if (!tripData) return;

      const currentParticipants =
        tripData?.locations.find((location) => location.id === locationId)
          ?.participants || [];
      updateLocation(locationId, {
        participants: currentParticipants.filter(
          (participant) => participant.id !== user.user_id
        ),
      });

      // Update local state
      setlocationParticipants(
        locationParticipants.filter((p) => p.user_id !== user.user_id)
      );
      // Recalculate budget
      calculateBudget(locationId);

      setIsDropdownOpen(false);
    },
    [
      tripData,
      locationId,
      updateLocation,
      locationParticipants,
      calculateBudget,
    ]
  );

  if (!tripData) return null;

  return (
    <div
      className="flex flex-col w-full"
      data-tutorial="overview-destination-users"
    >
      <div className="w-full bg-white rounded-lg flex flex-col justify-start p-3 2xl:p-4 shadow-sm border border-neutral-100">
        <div className="flex flex-row justify-between w-full items-center mb-3 2xl:mb-3">
          <div className="flex flex-row space-x-2 items-center">
            <Users className="w-4 h-4 2xl:w-[17px] 2xl:h-[17px] text-neutral-800" />
            <p className="text-sm 2xl:text-[15px] text-neutral-400 font-light">
              Destination Users
            </p>
          </div>
        </div>

        <div className="flex flex-row items-center flex-1 min-h-[2rem] 2xl:min-h-[2rem]">
          <div className="flex items-center">
            {locationParticipants.length > 0 ? (
              <div className="flex items-center">
                {locationParticipants.map((user, index) => (
                  <Tooltip
                    key={user.user_id}
                    content={
                      <div className="px-2 py-1 flex flex-col items-center justify-center gap-2">
                        <p className="text-xs 2xl:text-sm font-normal">
                          {user.first_name} {user.last_name}
                        </p>
                        <button
                          onClick={() => handleRemoveParticipant(user)}
                          className="flex items-center justify-center w-full text-neutral-700 text-xs 2xl:text-sm font-light hover:text-rose-600 hover:bg-rose-50 px-2 py-1 rounded-md space-x-1.5 transition-all ease-in-out duration-300"
                        >
                          <Trash className="w-3 h-3 2xl:w-3.5 2xl:h-3.5" />
                          <span>Remove</span>
                        </button>
                      </div>
                    }
                    placement="bottom"
                    radius="md"
                    showArrow={true}
                    closeDelay={150}
                    offset={10}
                  >
                    <div
                      className="relative group"
                      style={{
                        marginLeft: index === 0 ? "0" : "-0.75rem",
                        zIndex: locationParticipants.length - index,
                      }}
                    >
                      <div className="w-8 h-8 2xl:w-9 2xl:h-9 rounded-full overflow-hidden ring-2 ring-white bg-white">
                        <AvatarImage
                          src={user.profile_image}
                          firstName={user.first_name}
                          lastName={user.last_name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </Tooltip>
                ))}
                <CustomDropdown
                  trigger={
                    <div
                      className="relative group cursor-pointer"
                      style={{
                        marginLeft: "-0.50rem",
                        zIndex: 0,
                      }}
                    >
                      <div className="w-9 h-9 2xl:w-10 2xl:h-10 rounded-full overflow-hidden ring-2 ring-white bg-indigo-50 flex items-center justify-center">
                        <UserPlus2 className="w-4 h-4 2xl:w-[18px] 2xl:h-[18px] text-indigo-600" />
                      </div>
                    </div>
                  }
                  isOpen={isDropdownOpen}
                  setIsOpen={setIsDropdownOpen}
                  position="left"
                  size="sm"
                >
                  <div className="w-64 2xl:w-72 p-2 2xl:p-3">
                    {users.length > 0 ? (
                      <div className="flex flex-col space-y-1">
                        <p className="text-xs 2xl:text-sm font-medium text-neutral-500 px-2 pb-2">
                          Select users to add
                        </p>
                        {users.map((user) => {
                          const isParticipating = !availableUsers.some(
                            (u) => u.user_id === user.user_id
                          );
                          return (
                            <div
                              key={user.user_id}
                              className={cn(
                                "flex items-center gap-3 px-2 py-2 rounded-md hover:bg-neutral-50 transition-colors duration-200",
                                isParticipating
                                  ? "cursor-default"
                                  : "cursor-pointer"
                              )}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <div className="w-8 h-8 2xl:w-9 2xl:h-9 rounded-full overflow-hidden ring-2 ring-white">
                                <AvatarImage
                                  src={user.profile_image}
                                  firstName={user.first_name}
                                  lastName={user.last_name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex flex-col flex-1">
                                <span className="text-sm 2xl:text-[15px] text-neutral-800 font-light">
                                  {user.first_name} {user.last_name}
                                </span>
                              </div>
                              <span className="ml-auto">
                                {isParticipating ? (
                                  <div
                                    className="w-5 h-5 2xl:w-6 2xl:h-6 rounded-full bg-indigo-50 ring-1 ring-indigo-200 flex items-center justify-center hover:bg-rose-50 hover:ring-rose-200 group cursor-pointer transition-all duration-300 ease-in-out"
                                    onClick={(e) =>
                                      handleRemoveParticipant(user, e)
                                    }
                                  >
                                    <i className="fa-solid fa-check text-indigo-600 fa-xs 2xl:fa-sm transform transition-all duration-300 ease-in-out group-hover:scale-0 group-hover:rotate-90" />
                                    <i className="fa-solid fa-xmark text-rose-600 fa-xs 2xl:fa-sm absolute transform scale-0 -rotate-90 transition-all duration-300 ease-in-out group-hover:scale-100 group-hover:rotate-0" />
                                  </div>
                                ) : (
                                  <div
                                    className="w-5 h-5 2xl:w-6 2xl:h-6 rounded-full bg-neutral-50 ring-1 ring-neutral-200 flex items-center justify-center hover:bg-indigo-50 hover:ring-indigo-200 transition-all duration-300 ease-in-out"
                                    onClick={(e) =>
                                      handleAddParticipant(user, e)
                                    }
                                  >
                                    <i className="fa-solid fa-plus text-neutral-500 hover:text-indigo-600 transition-all duration-300 ease-in-out fa-xs 2xl:fa-sm" />
                                  </div>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center py-6 space-y-2">
                        <Users className="w-8 h-8 2xl:w-9 2xl:h-9 text-neutral-300" />
                        <p className="text-sm 2xl:text-[15px] text-neutral-400 font-medium">
                          No users available
                        </p>
                      </div>
                    )}
                  </div>
                </CustomDropdown>
              </div>
            ) : (
              <div className="flex items-center">
                <CustomDropdown
                  trigger={
                    <div
                      className="relative group cursor-pointer"
                      style={{
                        zIndex: 0,
                      }}
                    >
                      <div className="w-9 h-9 2xl:w-10 2xl:h-10 rounded-full overflow-hidden ring-2 ring-white bg-indigo-50 flex items-center justify-center">
                        <UserPlus2 className="w-4 h-4 2xl:w-[18px] 2xl:h-[18px] text-indigo-600" />
                      </div>
                    </div>
                  }
                  isOpen={isDropdownOpen}
                  setIsOpen={setIsDropdownOpen}
                  position="left"
                  size="sm"
                >
                  <div className="w-64 2xl:w-72 p-2 2xl:p-3">
                    {users.length > 0 ? (
                      <div className="flex flex-col space-y-1">
                        <p className="text-xs 2xl:text-sm font-medium text-neutral-500 px-2 pb-2">
                          Select users to add
                        </p>
                        {users.map((user) => {
                          const isParticipating = !availableUsers.some(
                            (u) => u.user_id === user.user_id
                          );
                          return (
                            <div
                              key={user.user_id}
                              className={cn(
                                "flex items-center gap-3 px-2 py-2 rounded-md hover:bg-neutral-50 transition-colors duration-200",
                                isParticipating
                                  ? "cursor-default"
                                  : "cursor-pointer"
                              )}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <div className="w-8 h-8 2xl:w-9 2xl:h-9 rounded-full overflow-hidden ring-2 ring-white">
                                <AvatarImage
                                  src={user.profile_image}
                                  firstName={user.first_name}
                                  lastName={user.last_name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex flex-col flex-1">
                                <span className="text-sm 2xl:text-[15px] text-neutral-800 font-medium">
                                  {user.first_name} {user.last_name}
                                </span>
                              </div>
                              <span className="ml-auto">
                                {isParticipating ? (
                                  <div
                                    className="w-5 h-5 2xl:w-6 2xl:h-6 rounded-full bg-indigo-50 ring-1 ring-indigo-200 flex items-center justify-center hover:bg-rose-50 hover:ring-rose-200 group cursor-pointer transition-all duration-300 ease-in-out"
                                    onClick={(e) =>
                                      handleRemoveParticipant(user, e)
                                    }
                                  >
                                    <i className="fa-solid fa-check text-indigo-600 fa-xs 2xl:fa-sm transform transition-all duration-300 ease-in-out group-hover:scale-0 group-hover:rotate-90" />
                                    <i className="fa-solid fa-xmark text-rose-600 fa-xs 2xl:fa-sm absolute transform scale-0 -rotate-90 transition-all duration-300 ease-in-out group-hover:scale-100 group-hover:rotate-0" />
                                  </div>
                                ) : (
                                  <div
                                    className="w-5 h-5 2xl:w-6 2xl:h-6 rounded-full bg-neutral-50 ring-1 ring-neutral-200 flex items-center justify-center hover:bg-indigo-50 hover:ring-indigo-200 transition-all duration-300 ease-in-out"
                                    onClick={(e) =>
                                      handleAddParticipant(user, e)
                                    }
                                  >
                                    <i className="fa-solid fa-plus text-neutral-500 hover:text-indigo-600 transition-all duration-300 ease-in-out fa-xs 2xl:fa-sm" />
                                  </div>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center py-6 space-y-2">
                        <Users className="w-8 h-8 2xl:w-9 2xl:h-9 text-neutral-300" />
                        <p className="text-sm 2xl:text-[15px] text-neutral-400 font-medium">
                          No users available
                        </p>
                      </div>
                    )}
                  </div>
                </CustomDropdown>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripParticipants;
